import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatApiErrorDetail, poundsToPence, penceToPounds } from "@/lib/api";
import { getMarket, fmt, pricingBreakdown, bookingFeeFor } from "@/config/markets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EVENT_FORM } from "@/constants/testIds";
import ImageUpload from "@/components/ImageUpload";
import {
  RECURRENCE_RULES, WEEKDAY_LABELS, POSITION_LABELS,
  computeOccurrenceDates, humanRule, toLocalDatetimeInput as toLocalDT,
} from "@/lib/recurrence";
import { Plus, Trash2, ArrowLeft, Copy, ChevronDown, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

const emptyTier = () => ({
  id: "",
  name: "General Admission",
  price_pounds: "15.00",
  quantity: 100,
  description: "",
  sold: 0,
});

const emptyDiscount = () => ({
  id: "",
  code: "",
  kind: "percent",
  amount: 10,
  max_uses: "",
  uses: 0,
  active: true,
});

const emptyQuestion = () => ({
  id: "",
  label: "",
  type: "text",
  required: false,
  options_text: "",
});

function toLocalDatetimeInput(iso) {
  return toLocalDT(iso ? new Date(iso) : null);
}

function OccurrenceRow({ idx, occ, baseTitle, baseImage, baseDescription, baseSalesStart, baseSalesEnd, override, onOverride, onReset }) {
  const [open, setOpen] = useState(false);
  const hasOverride = override && Object.values(override).some((v) => v !== null && v !== undefined && v !== "");
  const effectiveTitle = (override && override.title) || baseTitle || "(untitled)";

  return (
    <div className="p-4" data-testid={`occurrence-row-${idx}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left hover:text-zinc-900"
        >
          {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <div className="min-w-0">
            <div className="font-medium truncate flex items-center gap-2">
              #{idx + 1} · {effectiveTitle}
              {hasOverride && <span className="text-xs font-mono uppercase text-indigo-600 tracking-widest">Custom</span>}
            </div>
            <div className="text-xs text-zinc-500">
              {occ.start_at.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
            </div>
          </div>
        </button>
        {hasOverride && (
          <Button variant="ghost" size="sm" onClick={onReset} className="text-zinc-500">
            Reset
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-4 space-y-4 pl-7 border-l-2 border-zinc-100">
          <div>
            <Label>Title override</Label>
            <Input
              value={override?.title || ""}
              onChange={(e) => onOverride({ title: e.target.value || null })}
              placeholder={baseTitle}
              className="mt-2 h-10"
              data-testid={`occ-title-${idx}`}
            />
          </div>
          <div>
            <Label>Description override</Label>
            <Textarea
              value={override?.description ?? ""}
              onChange={(e) => onOverride({ description: e.target.value })}
              placeholder={baseDescription || "(same as main event)"}
              rows={3}
              className="mt-2"
              data-testid={`occ-desc-${idx}`}
            />
          </div>
          <div>
            <Label>Cover image (override)</Label>
            <ImageUpload
              value={override?.image_url ?? ""}
              onChange={(v) => onOverride({ image_url: v || null })}
              testId={`occ-image-${idx}`}
            />
            {!override?.image_url && baseImage && (
              <div className="mt-2 text-xs text-zinc-500">Uses the main event image if left empty.</div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Sales open from</Label>
              <Input
                type="datetime-local"
                value={override?.sales_start_at || ""}
                onChange={(e) => onOverride({ sales_start_at: e.target.value || null })}
                placeholder={baseSalesStart}
                className="mt-2 h-10"
                data-testid={`occ-sales-start-${idx}`}
              />
            </div>
            <div>
              <Label>Sales close at</Label>
              <Input
                type="datetime-local"
                value={override?.sales_end_at || ""}
                onChange={(e) => onOverride({ sales_end_at: e.target.value || null })}
                placeholder={baseSalesEnd}
                className="mt-2 h-10"
                data-testid={`occ-sales-end-${idx}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();

  const [form, setForm] = useState({
    title: "",
    description: "",
    image_url: "",
    venue_location: "",
    venue_location_lat: null,
    venue_location_lon: null,
    start_at: "",
    end_at: "",
    sales_start_at: "",
    sales_end_at: "",
    is_recurring: false,
    recurrence_rule: "weekly",
    recurrence_count: 4,
    recurrence_position: 1,
    recurrence_weekday: 5,
    occurrence_overrides: {},
    hide_booking_fee: false,
    status: "draft",
    access_code: "",
    access_codes: [],
    members_only_tier_ids: [],
    tiers: [emptyTier()],
    discount_codes: [],
    custom_questions: [],
    parent_event_id: null,
  });
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [venueCountry, setVenueCountry] = useState("GB");
  const [venueAccountType, setVenueAccountType] = useState("venue");
  const [membershipTiers, setMembershipTiers] = useState([]);

  const market = useMemo(() => getMarket(venueCountry === "US" ? "us" : "uk"), [venueCountry]);
  const currency = market.symbol;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/venues/me");
        setVenueCountry(data?.country || "GB");
        setVenueAccountType(data?.account_type || "venue");
      } catch (e) { /* stay on GB default */ }
      try {
        const { data: tiers } = await http.get("/venues/me/memberships/tiers");
        setMembershipTiers((tiers || []).filter((t) => t.status === "active"));
      } catch (e) { /* no memberships */ }
    })();
  }, []);

  const loadEvent = useCallback(async () => {
    if (!editing) return;
    try {
      const { data } = await http.get(`/events/${id}`);
      setForm({
        title: data.title || "",
        description: data.description || "",
        image_url: data.image_url || "",
        venue_location: data.venue_location || "",
        venue_location_lat: data.venue_location_lat ?? null,
        venue_location_lon: data.venue_location_lon ?? null,
        start_at: toLocalDatetimeInput(data.start_at),
        end_at: toLocalDatetimeInput(data.end_at),
        sales_start_at: toLocalDatetimeInput(data.sales_start_at),
        sales_end_at: toLocalDatetimeInput(data.sales_end_at),
        is_recurring: data.is_recurring || false,
        recurrence_rule: data.recurrence_rule || "weekly",
        recurrence_count: data.recurrence_count || 4,
        recurrence_position: data.recurrence_position ?? 1,
        recurrence_weekday: data.recurrence_weekday ?? 5,
        occurrence_overrides: {},
        hide_booking_fee: !!data.hide_booking_fee,
        status: data.status || "draft",
        access_code: data.access_code || "",
        access_codes: (data.access_codes || []).map((ac) => ({
          code: ac.code || "",
          tier_ids: Array.isArray(ac.tier_ids) ? ac.tier_ids : [],
        })),
        members_only_tier_ids: Array.isArray(data.members_only_tier_ids) ? data.members_only_tier_ids : [],
        tiers: (data.tiers && data.tiers.length ? data.tiers : [emptyTier()]).map((t) => ({
          id: t.id || "",
          name: t.name,
          price_pounds: penceToPounds(t.price_pence),
          quantity: t.quantity,
          description: t.description || "",
          sold: t.sold || 0,
        })),
        discount_codes: (data.discount_codes || []).map((d) => ({
          id: d.id || "",
          code: d.code || "",
          kind: d.kind || "percent",
          amount: d.kind === "fixed" ? penceToPounds(d.amount) : d.amount,
          max_uses: d.max_uses ?? "",
          uses: d.uses || 0,
          active: d.active !== false,
        })),
        custom_questions: (data.custom_questions || []).map((q) => ({
          id: q.id || "",
          label: q.label,
          type: q.type || "text",
          required: !!q.required,
          options_text: (q.options || []).join(", "),
        })),
        parent_event_id: data.parent_event_id || null,
      });
      // If recurring parent, load occurrences
      if ((data.is_recurring && !data.parent_event_id) || data.parent_event_id) {
        const { data: occs } = await http.get(`/events/${id}/occurrences`);
        setOccurrences(occs || []);
      } else {
        setOccurrences([]);
      }
    } catch (e) {
      toast.error("Could not load event");
    }
  }, [editing, id]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Tier helpers
  const updateTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, emptyTier()] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  // Discount helpers
  const updateDiscount = (i, k, v) => setForm((f) => ({ ...f, discount_codes: f.discount_codes.map((d, idx) => idx === i ? { ...d, [k]: v } : d) }));
  const addDiscount = () => setForm((f) => ({ ...f, discount_codes: [...f.discount_codes, emptyDiscount()] }));
  const removeDiscount = (i) => setForm((f) => ({ ...f, discount_codes: f.discount_codes.filter((_, idx) => idx !== i) }));

  // Access code helpers (multi-code, per-tier)
  const updateAccessCode = (i, k, v) => setForm((f) => ({ ...f, access_codes: f.access_codes.map((ac, idx) => idx === i ? { ...ac, [k]: v } : ac) }));
  const addAccessCode = () => setForm((f) => ({ ...f, access_codes: [...f.access_codes, { code: "", tier_ids: [] }] }));
  const removeAccessCode = (i) => setForm((f) => ({ ...f, access_codes: f.access_codes.filter((_, idx) => idx !== i) }));
  const toggleAccessCodeTier = (i, tierId) => setForm((f) => ({
    ...f,
    access_codes: f.access_codes.map((ac, idx) => {
      if (idx !== i) return ac;
      const has = (ac.tier_ids || []).includes(tierId);
      return { ...ac, tier_ids: has ? ac.tier_ids.filter((t) => t !== tierId) : [...(ac.tier_ids || []), tierId] };
    }),
  }));

  // Question helpers
  const updateQuestion = (i, k, v) => setForm((f) => ({ ...f, custom_questions: f.custom_questions.map((q, idx) => idx === i ? { ...q, [k]: v } : q) }));
  const addQuestion = () => setForm((f) => ({ ...f, custom_questions: [...f.custom_questions, emptyQuestion()] }));
  const removeQuestion = (i) => setForm((f) => ({ ...f, custom_questions: f.custom_questions.filter((_, idx) => idx !== i) }));

  // Occurrence override helpers
  const setOccOverride = (idx, patch) =>
    setForm((f) => ({
      ...f,
      occurrence_overrides: {
        ...f.occurrence_overrides,
        [idx]: { ...(f.occurrence_overrides[idx] || {}), ...patch },
      },
    }));
  const clearOccOverride = (idx) =>
    setForm((f) => {
      const cp = { ...f.occurrence_overrides };
      delete cp[idx];
      return { ...f, occurrence_overrides: cp };
    });

  // Preview generated occurrences (only meaningful during creation)
  const previewOccurrences = useMemo(() => {
    if (editing || !form.is_recurring || !form.start_at || !form.end_at) return [];
    return computeOccurrenceDates(form.start_at, form.end_at, {
      recurrence_rule: form.recurrence_rule,
      recurrence_count: form.recurrence_count,
      recurrence_position: form.recurrence_position,
      recurrence_weekday: form.recurrence_weekday,
    });
  }, [editing, form.is_recurring, form.start_at, form.end_at, form.recurrence_rule,
      form.recurrence_count, form.recurrence_position, form.recurrence_weekday]);

  const buildPayload = (targetStatus) => {
    const occs = (!editing && form.is_recurring)
      ? previewOccurrences.map((o, idx) => {
          const ov = form.occurrence_overrides[idx] || {};
          return {
            start_at: o.start_at.toISOString(),
            end_at: o.end_at.toISOString(),
            title: ov.title || null,
            description: ov.description ?? null,
            image_url: ov.image_url ?? null,
            sales_start_at: ov.sales_start_at ? new Date(ov.sales_start_at).toISOString() : null,
            sales_end_at: ov.sales_end_at ? new Date(ov.sales_end_at).toISOString() : null,
          };
        })
      : [];

    return {
      title: form.title,
      description: form.description,
      image_url: form.image_url || null,
      venue_location: form.venue_location,
      venue_location_lat: form.venue_location_lat,
      venue_location_lon: form.venue_location_lon,
      start_at: new Date(form.start_at).toISOString(),
      end_at: new Date(form.end_at).toISOString(),
      sales_start_at: form.sales_start_at ? new Date(form.sales_start_at).toISOString() : null,
      sales_end_at: form.sales_end_at ? new Date(form.sales_end_at).toISOString() : null,
      is_recurring: form.is_recurring,
      recurrence_rule: form.is_recurring ? form.recurrence_rule : null,
      recurrence_count: form.is_recurring ? Number(form.recurrence_count) : null,
      recurrence_position: form.is_recurring && form.recurrence_rule === "monthly_weekday" ? Number(form.recurrence_position) : null,
      recurrence_weekday: form.is_recurring && form.recurrence_rule === "monthly_weekday" ? Number(form.recurrence_weekday) : null,
      occurrences: occs,
      access_code: form.access_code ? form.access_code.trim() : null,
      access_codes: form.access_codes
        .map((ac) => ({
          code: (ac.code || "").trim().toUpperCase(),
          tier_ids: Array.isArray(ac.tier_ids) ? ac.tier_ids.filter(Boolean) : [],
        }))
        .filter((ac) => ac.code),
      members_only_tier_ids: (form.members_only_tier_ids || []).filter(Boolean),
      hide_booking_fee: !!form.hide_booking_fee,
      status: targetStatus,
      tiers: form.tiers.map((t) => ({
        id: t.id || "",
        name: t.name,
        price_pence: poundsToPence(t.price_pounds),
        quantity: Number(t.quantity),
        description: t.description || null,
        sold: t.sold || 0,
      })),
      discount_codes: form.discount_codes.map((d) => ({
        id: d.id || "",
        code: (d.code || "").trim().toUpperCase(),
        kind: d.kind,
        amount: d.kind === "fixed" ? poundsToPence(d.amount) : Number(d.amount),
        max_uses: d.max_uses === "" ? null : Number(d.max_uses),
        uses: d.uses || 0,
        active: d.active !== false,
      })).filter((d) => d.code),
      custom_questions: form.custom_questions.map((q) => ({
        id: q.id || "",
        label: q.label,
        type: q.type,
        required: !!q.required,
        options: q.type === "select" ? (q.options_text || "").split(",").map((s) => s.trim()).filter(Boolean) : [],
      })).filter((q) => q.label),
    };
  };

  const save = async (targetStatus) => {
    setError("");
    setLoading(true);
    try {
      const payload = buildPayload(targetStatus);
      if (editing) {
        await http.patch(`/events/${id}`, payload);
        toast.success("Event updated");
        await loadEvent();
      } else {
        const { data } = await http.post("/events", payload);
        toast.success(data.occurrence_ids && data.occurrence_ids.length > 1
          ? `Series created — ${data.occurrence_ids.length} occurrences`
          : "Event created");
        nav(`/dashboard/events/${data.id}/edit`, { replace: true });
        return;
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(occurrences.length > 1 && !form.parent_event_id
      ? `Delete this event AND all ${occurrences.length - 1} occurrences?`
      : "Delete this event?")) return;
    try {
      await http.delete(`/events/${id}`);
      toast.success("Deleted");
      nav("/dashboard/events");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const isChildOccurrence = Boolean(form.parent_event_id);
  const isParentWithOccurrences = !form.parent_event_id && occurrences.length > 1;

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <Link to="/dashboard/events" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4" /> Back to events
      </Link>
      <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">
            {editing ? "Manage event" : "Create event"}
          </h1>
          {isChildOccurrence && (
            <div className="mt-1 text-sm text-indigo-600 font-medium">
              Editing a single occurrence of a series — changes here only affect this date.
            </div>
          )}
          {isParentWithOccurrences && (
            <div className="mt-1 text-sm text-zinc-500">
              Series parent — this affects all {occurrences.length} occurrences unless you edit each individually below.
            </div>
          )}
        </div>
        {editing && (
          <Button variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete
          </Button>
        )}
      </div>

      <div className="mt-8 space-y-6">
        {/* DETAILS */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6 space-y-5">
          <h2 className="font-display font-bold text-lg">Details</h2>
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => update("title", e.target.value)} data-testid={EVENT_FORM.title} className="mt-2 h-11" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} data-testid={EVENT_FORM.description} className="mt-2" rows={4} />
          </div>
          <div>
            <Label>
              Location {venueAccountType === "promoter" && <span className="text-red-600">*</span>}
            </Label>
            {venueAccountType === "promoter" && (
              <div className="text-xs text-zinc-500 mt-0.5">Each event can be at a different place — search below.</div>
            )}
            <div className="mt-2">
              <LocationAutocomplete
                value={form.venue_location}
                onChange={(v) => update("venue_location", v)}
                onCoords={(lat, lon) => {
                  update("venue_location_lat", lat);
                  update("venue_location_lon", lon);
                }}
                testId={EVENT_FORM.location}
                placeholder={venueAccountType === "promoter" ? "e.g. Village Underground, London" : "Address or venue name"}
                required={venueAccountType === "promoter"}
              />
            </div>
          </div>
          <div>
            <Label>Cover image</Label>
            <ImageUpload value={form.image_url} onChange={(v) => update("image_url", v || "")} testId={EVENT_FORM.imageUpload} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Starts</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => update("start_at", e.target.value)} data-testid={EVENT_FORM.startAt} className="mt-2 h-11" />
            </div>
            <div>
              <Label>Ends</Label>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => update("end_at", e.target.value)} data-testid={EVENT_FORM.endAt} className="mt-2 h-11" />
            </div>
          </div>
        </section>

        {/* SALES WINDOW */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6 space-y-4">
          <div>
            <h2 className="font-display font-bold text-lg">Sales window</h2>
            <p className="text-sm text-zinc-500 mt-1">Control exactly when tickets are on sale. Leave blank for immediate & indefinite sales.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Sales open from</Label>
              <Input type="datetime-local" value={form.sales_start_at} onChange={(e) => update("sales_start_at", e.target.value)} data-testid={EVENT_FORM.salesStartAt} className="mt-2 h-11" />
            </div>
            <div>
              <Label>Sales close at</Label>
              <Input type="datetime-local" value={form.sales_end_at} onChange={(e) => update("sales_end_at", e.target.value)} data-testid={EVENT_FORM.salesEndAt} className="mt-2 h-11" />
            </div>
          </div>
        </section>

        {/* BOOKING FEE DISPLAY */}
        <section className="rounded-xl bg-white border-2 border-emerald-200 p-6" data-testid="event-hide-booking-fee-section">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-bold text-lg">Hide booking fee at checkout</h2>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-widest">
                  Recommended
                </span>
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                Our fee is <strong>{Math.round(market.bookingFeePercent * 100)}% of face value</strong> per ticket with a <strong>{fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum</strong>. Leave this off and the buyer sees the fee as a separate line at checkout. Turn it on to bake the fee into the sticker price — buyers see a single, all-in number.
              </p>
            </div>
            <Switch
              checked={form.hide_booking_fee}
              onCheckedChange={(v) => update("hide_booking_fee", v)}
              data-testid="event-hide-booking-fee"
            />
          </div>
          {form.hide_booking_fee ? (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
              ✓ Clean checkout enabled. Each tier below will be shown to buyers at <strong>base price + booking fee</strong> as a single all-in price. Your dashboard prices stay as the base — we uplift on the public event page automatically.
            </div>
          ) : (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="event-hide-booking-fee-warning">
              <strong>Heads up —</strong> the booking fee will appear as a separate <strong>per-ticket line item</strong> at checkout (5% of face value with a {fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum). Most venues prefer to hide this for a cleaner checkout.
            </div>
          )}
        </section>

        {/* RECURRING (only allowed on create) */}
        {!editing && (
          <section className="rounded-xl bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-lg">Recurring</h2>
                <p className="text-sm text-zinc-500">Each occurrence can be themed and priced independently.</p>
              </div>
              <Switch checked={form.is_recurring} onCheckedChange={(v) => update("is_recurring", v)} data-testid={EVENT_FORM.recurring} />
            </div>

            {form.is_recurring && (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mt-5">
                  <div>
                    <Label>Repeats</Label>
                    <Select value={form.recurrence_rule} onValueChange={(v) => update("recurrence_rule", v)}>
                      <SelectTrigger className="mt-2 h-11" data-testid={EVENT_FORM.recurrenceRule}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RECURRENCE_RULES.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Number of occurrences</Label>
                    <Input type="number" min="1" max="52" value={form.recurrence_count} onChange={(e) => update("recurrence_count", e.target.value)} data-testid={EVENT_FORM.recurrenceCount} className="mt-2 h-11" />
                  </div>
                </div>

                {form.recurrence_rule === "monthly_weekday" && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Position</Label>
                      <Select value={String(form.recurrence_position)} onValueChange={(v) => update("recurrence_position", Number(v))}>
                        <SelectTrigger className="mt-2 h-11" data-testid="event-recurrence-position"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(POSITION_LABELS).map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Weekday</Label>
                      <Select value={String(form.recurrence_weekday)} onValueChange={(v) => update("recurrence_weekday", Number(v))}>
                        <SelectTrigger className="mt-2 h-11" data-testid="event-recurrence-weekday"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {WEEKDAY_LABELS.map((wd, idx) => (
                            <SelectItem key={idx} value={String(idx)}>{wd}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {form.recurrence_rule === "monthly_weekday" && (
                  <div className="mt-3 text-sm text-zinc-500">
                    {humanRule({
                      recurrence_rule: form.recurrence_rule,
                      recurrence_position: form.recurrence_position,
                      recurrence_weekday: form.recurrence_weekday,
                    })} · {form.recurrence_count} occurrences
                  </div>
                )}

                {previewOccurrences.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarIcon className="w-4 h-4 text-zinc-500" />
                      <h3 className="font-semibold">Preview & customise {previewOccurrences.length} occurrence{previewOccurrences.length === 1 ? "" : "s"}</h3>
                    </div>
                    <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-200">
                      {previewOccurrences.map((occ, idx) => (
                        <OccurrenceRow
                          key={idx}
                          idx={idx}
                          occ={occ}
                          baseTitle={form.title}
                          baseImage={form.image_url}
                          baseDescription={form.description}
                          baseSalesStart={form.sales_start_at}
                          baseSalesEnd={form.sales_end_at}
                          override={form.occurrence_overrides[idx]}
                          onOverride={(patch) => setOccOverride(idx, patch)}
                          onReset={() => clearOccOverride(idx)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* OCCURRENCES editor (parent view) */}
        {isParentWithOccurrences && (
          <section className="rounded-xl bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg">Series occurrences</h2>
                <p className="text-sm text-zinc-500">Each date can have its own theme — title, image, description, tiers.</p>
              </div>
            </div>
            <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-200">
              {occurrences.map((occ) => (
                <div key={occ.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={EVENT_FORM.occurrenceRow(occ.id)}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      #{(occ.series_index ?? 0) + 1} · {occ.title}
                      {occ.id === id && <span className="ml-2 text-xs uppercase tracking-widest text-indigo-600">Current</span>}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {new Date(occ.start_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                  {occ.id !== id && (
                    <Link to={`/dashboard/events/${occ.id}/edit`}>
                      <Button size="sm" variant="outline" className="rounded-full">
                        <Copy className="w-3.5 h-3.5 mr-1" /> Customise
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* TICKETS */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display font-bold text-lg">Ticket tiers</h2>
            <Button onClick={addTier} variant="outline" size="sm" className="rounded-full" data-testid={EVENT_FORM.addTier}>
              <Plus className="w-4 h-4 mr-1" /> Add tier
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            <strong>Per-ticket pricing.</strong> No maximum ticket price. Our booking fee is <strong>{Math.round(market.bookingFeePercent * 100)}% of face value</strong> with a <strong>{fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum</strong>, paid by the buyer at checkout — you always keep <strong>100%</strong> of your list price.
          </p>
          <div className="space-y-3">
            {form.tiers.map((t, i) => {
              const priceNum = parseFloat(t.price_pounds) || 0;
              const b = pricingBreakdown(market, priceNum);
              return (
              <div key={i} className="rounded-lg border p-4 border-zinc-200">
                <div className="grid sm:grid-cols-12 gap-3 items-end">
                  <div className="sm:col-span-5">
                    <Label>Name</Label>
                    <Input value={t.name} onChange={(e) => updateTier(i, "name", e.target.value)} data-testid={EVENT_FORM.tierName(i)} className="mt-2 h-10" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Price ({currency})</Label>
                    <div className="mt-2 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">{currency}</span>
                      <Input type="number" step="0.01" min="0" value={t.price_pounds} onChange={(e) => updateTier(i, "price_pounds", e.target.value)} data-testid={EVENT_FORM.tierPrice(i)} className="h-10 pl-6" />
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Quantity</Label>
                    <Input type="number" min="1" value={t.quantity} onChange={(e) => updateTier(i, "quantity", e.target.value)} data-testid={EVENT_FORM.tierQty(i)} className="mt-2 h-10" />
                  </div>
                  <div className="sm:col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeTier(i)} data-testid={EVENT_FORM.tierRemove(i)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Per-ticket breakdown */}
                {priceNum > 0 && (
                  <div
                    className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4"
                    data-testid={`tier-breakdown-${i}`}
                  >
                    {/* You get */}
                    <div className="rounded-lg bg-white border-l-4 border-emerald-500 pl-3 pr-4 py-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">In your bank</div>
                      <div className="mt-1 font-display font-black text-2xl tabular-nums text-emerald-900" data-testid={`tier-you-get-${i}`}>
                        {fmt(market, b.venueReceives, { decimals: 2 })}
                      </div>
                      <div className="mt-1 text-[11px] text-zinc-500">100% of face value</div>
                    </div>

                    {/* Customer sees */}
                    <div className="rounded-lg bg-white border-l-4 border-indigo-500 pl-3 pr-4 py-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-700">Customer sees</div>
                      <div className="mt-1 font-display font-black text-2xl tabular-nums text-indigo-900" data-testid={`tier-customer-sees-${i}`}>
                        {fmt(market, b.buyerTotal, { decimals: 2 })}
                      </div>
                      <div className="mt-1 text-[11px] text-zinc-500">
                        {fmt(market, priceNum, { decimals: 2 })} + {fmt(market, b.bookingFee, { decimals: 2 })} fee
                      </div>
                    </div>

                    {/* What we make */}
                    <div className="rounded-lg bg-white border-l-4 border-zinc-900 pl-3 pr-4 py-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">What we make</div>
                      <div className="mt-1 font-display font-black text-2xl tabular-nums text-zinc-900 whitespace-nowrap" data-testid={`tier-what-we-make-${i}`}>
                        {fmt(market, b.ourNetLow, { decimals: 2 })}–{fmt(market, b.ourNetHigh, { decimals: 2 })}
                      </div>
                      <div className="mt-1 text-[11px] text-zinc-500">after card fees</div>
                    </div>

                    {/* What we share with you */}
                    <div className="rounded-lg bg-white border-l-4 border-rose-500 pl-3 pr-4 py-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700">We cover</div>
                      <div className="mt-1 font-display font-black text-2xl tabular-nums text-rose-900 whitespace-nowrap" data-testid={`tier-we-cover-${i}`}>
                        {fmt(market, b.weCoverLow, { decimals: 2 })}–{fmt(market, b.weCoverHigh, { decimals: 2 })}
                      </div>
                      <div className="mt-1 text-[11px] text-zinc-500">card + payout fees</div>
                    </div>
                  </div>
                )}
                {t.sold > 0 && (
                  <div className="mt-2 text-xs text-zinc-500">{t.sold} sold so far.</div>
                )}
              </div>
            );})}
          </div>
        </section>

        {/* ACCESS CODES */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6" data-testid="access-codes-section">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="font-display font-bold text-lg">Access codes (optional)</h2>
              <p className="text-sm text-zinc-500 mt-1">Hide the event behind a password. Different codes can unlock different ticket types — e.g. <code>VIP2026</code> unlocks VIP + Backstage, <code>PATRON</code> only unlocks the Patron tier.</p>
            </div>
            <Button
              onClick={addAccessCode}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="add-access-code-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add code
            </Button>
          </div>
          <div className="space-y-3 mt-4">
            {form.access_codes.length === 0 && (
              <div className="text-sm text-zinc-500">No access codes — event is public.</div>
            )}
            {form.access_codes.map((ac, i) => {
              const allSelected = (ac.tier_ids || []).length === 0;
              return (
                <div key={i} className="rounded-lg border border-zinc-200 p-4" data-testid={`access-code-row-${i}`}>
                  <div className="grid sm:grid-cols-12 gap-3 items-start">
                    <div className="sm:col-span-4">
                      <Label>Code</Label>
                      <Input
                        value={ac.code}
                        onChange={(e) => updateAccessCode(i, "code", e.target.value.toUpperCase())}
                        placeholder="VIP2026"
                        className="mt-2 h-10 font-mono"
                        data-testid={`access-code-input-${i}`}
                      />
                    </div>
                    <div className="sm:col-span-7">
                      <Label>Unlocks which ticket types?</Label>
                      <div className="mt-2 space-y-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => updateAccessCode(i, "tier_ids", [])}
                            data-testid={`access-code-alltiers-${i}`}
                            className="rounded border-zinc-300"
                          />
                          <span className="font-medium">All ticket types</span>
                        </label>
                        {form.tiers.map((t) => {
                          if (!t.id && !t.name) return null;
                          const tierKey = t.id || t.name;
                          const checked = (ac.tier_ids || []).includes(tierKey);
                          return (
                            <label
                              key={tierKey}
                              className={`flex items-center gap-2 text-sm cursor-pointer ${allSelected ? "opacity-40" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={allSelected}
                                onChange={() => toggleAccessCodeTier(i, tierKey)}
                                data-testid={`access-code-tier-${i}-${tierKey}`}
                                className="rounded border-zinc-300"
                              />
                              <span>{t.name || "Untitled tier"}</span>
                            </label>
                          );
                        })}
                        {form.tiers.length === 0 && (
                          <div className="text-xs text-zinc-500">Add ticket tiers above first.</div>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-1 flex sm:justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAccessCode(i)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`access-code-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {form.access_code && !form.access_codes.length && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              You have a legacy single-code access code (<code className="font-mono">{form.access_code}</code>) — it still works and unlocks all ticket types. Add codes above to migrate to the new per-tier system.
            </div>
          )}
        </section>

        {/* MEMBERS-ONLY */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6" data-testid="members-only-section">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display font-bold text-lg">Members-only event (optional)</h2>
              <p className="text-sm text-zinc-500 mt-1">Restrict ticket sales to active members of specific tiers. Buyers will need to verify their membership email at checkout.</p>
            </div>
            <Switch
              checked={(form.members_only_tier_ids || []).length > 0}
              onCheckedChange={(v) => update("members_only_tier_ids", v ? (membershipTiers[0] ? [membershipTiers[0].id] : []) : [])}
              data-testid="members-only-toggle"
            />
          </div>

          {(form.members_only_tier_ids || []).length > 0 && (
            <div className="mt-4">
              {membershipTiers.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  You don&apos;t have any active membership tiers yet — <Link to="/dashboard/members" className="underline">create one first</Link>.
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Only active members of these tiers can buy tickets:</Label>
                  {membershipTiers.map((t) => {
                    const checked = (form.members_only_tier_ids || []).includes(t.id);
                    return (
                      <label
                        key={t.id}
                        data-testid={`members-only-tier-${t.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-zinc-300 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = new Set(form.members_only_tier_ids || []);
                            if (cur.has(t.id)) cur.delete(t.id); else cur.add(t.id);
                            update("members_only_tier_ids", Array.from(cur));
                          }}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{t.label}</div>
                          <div className="text-xs text-zinc-500">{currency}{(t.price_pence / 100).toFixed(2)} / {t.interval.replace("_", " ")}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        {/* DISCOUNT CODES */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-lg">Discount codes</h2>
              <p className="text-sm text-zinc-500">Percentage or fixed-amount discounts, applied at checkout.</p>
            </div>
            <Button onClick={addDiscount} variant="outline" size="sm" className="rounded-full" data-testid={EVENT_FORM.addDiscount}>
              <Plus className="w-4 h-4 mr-1" /> Add code
            </Button>
          </div>
          <div className="space-y-3">
            {form.discount_codes.length === 0 && (
              <div className="text-sm text-zinc-500">No discount codes yet.</div>
            )}
            {form.discount_codes.map((d, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-4">
                <div className="grid sm:grid-cols-12 gap-3 items-end">
                  <div className="sm:col-span-4">
                    <Label>Code</Label>
                    <Input value={d.code} onChange={(e) => updateDiscount(i, "code", e.target.value.toUpperCase())} placeholder="SUMMER10" data-testid={EVENT_FORM.discountCode(i)} className="mt-2 h-10 font-mono" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Type</Label>
                    <Select value={d.kind} onValueChange={(v) => updateDiscount(i, "kind", v)}>
                      <SelectTrigger className="mt-2 h-10" data-testid={EVENT_FORM.discountKind(i)}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percent</SelectItem>
                        <SelectItem value="fixed">Fixed ({currency})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>{d.kind === "percent" ? "Off %" : `Off (${currency})`}</Label>
                    <Input type="number" step={d.kind === "percent" ? "1" : "0.01"} min="0" value={d.amount} onChange={(e) => updateDiscount(i, "amount", e.target.value)} data-testid={EVENT_FORM.discountAmount(i)} className="mt-2 h-10" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Max uses</Label>
                    <Input type="number" min="0" placeholder="∞" value={d.max_uses} onChange={(e) => updateDiscount(i, "max_uses", e.target.value)} data-testid={EVENT_FORM.discountMaxUses(i)} className="mt-2 h-10" />
                  </div>
                  <div className="sm:col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeDiscount(i)} data-testid={EVENT_FORM.discountRemove(i)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {d.uses > 0 && (
                  <div className="mt-2 text-xs text-zinc-500">
                    Used {d.uses}{d.max_uses ? ` / ${d.max_uses}` : ""} time{d.uses === 1 ? "" : "s"}.
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CUSTOM QUESTIONS */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-lg">Buyer questions</h2>
              <p className="text-sm text-zinc-500">Name + email are collected by default. Add your own custom fields here.</p>
            </div>
            <Button onClick={addQuestion} variant="outline" size="sm" className="rounded-full" data-testid={EVENT_FORM.addQuestion}>
              <Plus className="w-4 h-4 mr-1" /> Add question
            </Button>
          </div>
          <div className="space-y-3">
            {form.custom_questions.length === 0 && (
              <div className="text-sm text-zinc-500">No custom questions yet.</div>
            )}
            {form.custom_questions.map((q, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-4">
                <div className="grid sm:grid-cols-12 gap-3 items-end">
                  <div className="sm:col-span-5">
                    <Label>Question / label</Label>
                    <Input value={q.label} onChange={(e) => updateQuestion(i, "label", e.target.value)} placeholder="e.g. Dietary requirements" data-testid={EVENT_FORM.questionLabel(i)} className="mt-2 h-10" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Type</Label>
                    <Select value={q.type} onValueChange={(v) => updateQuestion(i, "type", v)}>
                      <SelectTrigger className="mt-2 h-10" data-testid={EVENT_FORM.questionType(i)}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Short text</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="select">Dropdown</SelectItem>
                        <SelectItem value="checkbox">Yes / No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3 flex items-end gap-2 pb-2">
                    <Checkbox id={`req-${i}`} checked={q.required} onCheckedChange={(v) => updateQuestion(i, "required", !!v)} data-testid={EVENT_FORM.questionRequired(i)} />
                    <Label htmlFor={`req-${i}`} className="cursor-pointer">Required</Label>
                  </div>
                  <div className="sm:col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeQuestion(i)} data-testid={EVENT_FORM.questionRemove(i)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {q.type === "select" && (
                  <div className="mt-3">
                    <Label>Options (comma-separated)</Label>
                    <Input value={q.options_text} onChange={(e) => updateQuestion(i, "options_text", e.target.value)} placeholder="Vegan, Vegetarian, Gluten-free, None" data-testid={EVENT_FORM.questionOptions(i)} className="mt-2 h-10" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={() => save("draft")} disabled={loading} data-testid={EVENT_FORM.saveDraft} className="rounded-full">
            Save as draft
          </Button>
          <Button onClick={() => save("published")} disabled={loading} data-testid={EVENT_FORM.publish} className="rounded-full bg-zinc-900 hover:bg-zinc-800">
            {loading ? "Saving…" : (editing && form.status === "published" ? "Update" : "Publish")}
          </Button>
        </div>
      </div>
    </div>
  );
}
